import { Stack, Typography } from '@mui/material'
import { Button } from 'ui-components'
import { putSelectedRepairShop } from '@src/api/repairShopApi'
import SelectedRepairShopInfo from '@src/components/SelectedRepairShopInfo'
import SvvSpinner from '@src/components/SvvSpinner'
import UserInfo from '@src/components/UserInfo'
import useRepairShopMatch from '@src/hooks/useRepairShopMatch'
import { getErrorPath } from '@src/utils/errorPathUtils'
import { isValidPhone } from '@src/utils/isValidPhone'
import { removeSpaces } from '@src/utils/removeSpaces'
import React, { Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

interface SubmitButtonProps {
  submitHandler: (e: React.SyntheticEvent) => void
  isValid: boolean
}

const SubmitButton = ({
  submitHandler,
  isValid,
}: SubmitButtonProps): JSX.Element => {
  const { t } = useTranslation('repairshop')
  return (
    <Stack
      sx={{
        py: 2,
        px: 3,
        margin: 'auto',
        maxWidth: 'lg',
      }}
    >
      <Button
        variant="contained"
        color={'primaryLight'}
        type="submit"
        onClick={submitHandler}
        sx={{
          outline: 0,
          backgroundColor: 'secondary',
          py: 1,
          border: 'none',
          '&.MuiButton-root:disabled': {
            border: 'none',
            outline: 0,
          },
        }}
        disabled={!isValid}
      >
        {t('pre-commit-page.send-information')}
      </Button>
    </Stack>
  )
}

const PreCommitPage = () => {
  const { uuid } = useParams<string>()
  const { selectedRepairShop, loading, setLoading, userData, setUserData } =
    useRepairShopMatch(uuid)
  const navigate = useNavigate()

  const isValid = isValidPhone(userData.phoneNumber)

  const { t } = useTranslation('repairshop')

  if (loading) {
    return <SvvSpinner />
  } else if (!uuid || !selectedRepairShop) {
    navigate(getErrorPath(uuid))
  } else {
    const changeHandler = (
      e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
      if (e.target.name === 'phoneNumber') {
        setUserData((prevState) => ({
          ...prevState,
          phoneNumber: removeSpaces(e.target.value),
        }))
      } else {
        setUserData((prevState) => ({
          ...prevState,
          [e.target.name]: e.target.value,
        }))
      }
    }
    const submitHandler = (e: React.SyntheticEvent) => {
      e.preventDefault()
      setLoading(true)
      putSelectedRepairShop(uuid, userData)
        .then((res) => {
          if (res.selectionType === 'ALREADY_CONFIRMED') {
            navigate(`/${uuid}/valmis`)
          } else {
            navigate(`/${uuid}/korjaamo-valittu`)
          }
        })
        .catch((error) => navigate(getErrorPath(uuid, error.status)))
        .finally(() => setLoading(false))
    }
    return (
      <Fragment>
        <SelectedRepairShopInfo uuid={uuid} repairShop={selectedRepairShop} />
        <form>
          <UserInfo
            changeHandler={changeHandler}
            userData={userData}
            isValid={isValid}
          />
          <Typography
            variant="body2"
            sx={{ px: 3, py: 2, color: 'common.dark', fontSize: 14 }}
          >
            {t('pre-commit-page.sending-your-info')}
          </Typography>
          <SubmitButton submitHandler={submitHandler} isValid={isValid} />
        </form>
      </Fragment>
    )
  }
  return <Fragment></Fragment>
}

export default PreCommitPage
